<template>
    <div>
        <ErrorAlert :message="message" :error="error" />
        <StageComplete v-if="stageComplete" :message="message" :error="error" />

        <div v-else>
            <div class="card card-body border-0">
                <h6 class="font-weight-bold mb-4">Business Documents</h6>

                <!-- Entity Type Selector -->
                <div class="form-group">
                    <label for="documentType">Select Entity Type</label>
                    <select
                        v-model="selectedEntityType"
                        @change="populateDocumentLabels"
                        class="form-control"
                        id="documentType"
                        :disabled="documentsSubmitted"
                    >
                        <option disabled value="">-- Select Entity Type --</option>
                        <option value="companies">Companies</option>
                        <option value="registered_charities">Registered Charities</option>
                        <option value="unregistered_charities">Unregistered Charities</option>
                    </select>
                </div>

                <!-- Document Upload/Display Section -->
                <div class="document-section">
                    <div class="row mb-3" v-for="(label, index) in documentLabels" :key="index">
                        <div class="col-lg-4 col-12 d-flex align-items-center">
                            <label class="form-label">{{ label }}</label>
                        </div>
                        <div class="col-lg-8 col-12">
                            <!-- File Upload (when not submitted) -->
                            <FileUpload
                                v-if="!documentsSubmitted"
                                fileType="pdf"
                                url="/business/biller-onboarding/documents"
                                v-model="documentForm.documents[label]"
                                :documentType="label"
                            />

                            <!-- Document Preview (when submitted) -->
                            <div v-else class="submitted-document">
                                <template v-if="submittedDocuments[label]">
                                    <div class="document-preview">
                                        <i class="fas fa-file-pdf document-icon"></i>
                                        <span class="document-name">{{ submittedDocuments[label].original_name || label }}</span>
                                    </div>
                                    <a
                                        :href="submittedDocuments[label].url"
                                        target="_blank"
                                        class="btn btn-sm btn-outline-primary view-btn"
                                    >
                                        <i class="fas fa-eye"></i> View
                                    </a>
                                </template>
                                <span v-else class="text-muted">No document uploaded</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Submit Button -->
            <div class="d-flex justify-content-between my-4" v-if="!documentsSubmitted">
                <button
                    class="btn btn-primary btn-block"
                    :class="{ 'btn-loading': loading }"
                    @click="submitDocuments"
                    :disabled="!selectedEntityType || !allDocumentsUploaded"
                >
                    Submit
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../../core/forms/form.js";
import FileUpload from "../components/FileUpload.vue";
import StageComplete from "../components/StageComplete.vue";
import ErrorAlert from "../components/ErrorAlert.vue";
import axios from "axios";

export default {
    name: "BusinessDocuments",
    components: {
        ErrorAlert,
        StageComplete,
        FileUpload,
    },
    props: ['stage', 'message'],
    data() {
        return {
            selectedEntityType: "",
            documentLabels: [],
            companies: [
                'Certificate of Incorporation',
                'CR6 Form (formerly CR14 Form)',
                'Board Resolution',
                'Bank Account Verification Letter'
            ],
            registered_charities: [
                'Certificate of Registration',
                'Official List of Trustees or Board Members',
                'Signed Board Resolution',
                'Bank Account Verification Letter',
                'Beneficiary Information'
            ],
            unregistered_charities: [
                'Trust Deed or Constitution',
                'Signed Resolution',
                'Bank Account Verification Letter',
                'Beneficiary Information'
            ],
            documentForm: new Form({
                documents: {},
                document_type: [],
            }),
            submittedDocuments: {}, // Stores submitted document data
            stageComplete: false,
            message: "",
            error: false,
            loading: false,
            documentsSubmitted: false,
        };
    },
    computed: {
        allDocumentsUploaded() {
            return this.documentLabels.every(
                label => this.documentForm.documents[label] || this.submittedDocuments[label]
            );
        },
    },
    methods: {
        populateDocumentLabels() {
            if (this.selectedEntityType === "companies") {
                this.documentLabels = [...this.companies];
            } else if (this.selectedEntityType === "registered_charities") {
                this.documentLabels = [...this.registered_charities];
            } else if (this.selectedEntityType === "unregistered_charities") {
                this.documentLabels = [...this.unregistered_charities];
            } else {
                this.documentLabels = [];
            }

            // Initialize empty documents for each label
            this.documentLabels.forEach(label => {
                if (!this.documentForm.documents[label]) {
                    this.$set(this.documentForm.documents, label, null);
                }
            });
        },

        async submitDocuments() {
            this.loading = true;
            this.error = false;
            this.message = "";

            this.documentForm.fill("document_type", this.documentLabels);

            try {
                const response = await this.documentForm.submit(`/business/biller-onboarding/check-documents`);
                this.stageComplete = true;
                this.message = response.data.message;
                this.documentsSubmitted = true;

                // Store the submitted documents for display
                this.submittedDocuments = {...this.documentForm.documents};

                if (this.stageComplete === true) {
                    setTimeout(() => {
                        this.stageComplete = false;
                        this.message = "";
                        this.$emit("update-stage", this.stage + 1);
                    }, 3000);
                }
            } catch (error) {
                this.error = true;
                const errors = error.response?.data?.errors?.missing_document_types || [];
                if (errors.length > 0) {
                    this.message = `The following documents are missing:\n- ${errors.join("\n- ")}`;
                } else {
                    this.message = "An error occurred while checking documents.";
                }
            } finally {
                this.loading = false;
            }
        },

        loadSubmittedDocuments() {
            axios.get("/business/biller-onboarding/get-documents")
                .then(response => {
                    const data = response.data.data || response.data;
                    if (data.entity_type) {
                        this.selectedEntityType = data.entity_type;
                        this.populateDocumentLabels();

                        if (data.documents) {
                            // Transform the documents array into an object keyed by document type
                            const documentsObj = {};
                            data.documents.forEach(doc => {
                                documentsObj[doc.document_type] = {
                                    url: doc.file_url,
                                    original_name: doc.original_name
                                };
                            });
                            this.submittedDocuments = documentsObj;
                            this.documentsSubmitted = true;
                        }
                    }
                })
                .catch(error => {
                    console.error("Error loading submitted documents:", error);
                });
        },
    },
    mounted() {
        this.loadSubmittedDocuments();
    }
};
</script>

<style scoped>
.document-section {
    margin-top: 20px;
}

.submitted-document {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #e9ecef;
    min-height: 45px;
}

.document-preview {
    display: flex;
    align-items: center;
    flex-grow: 1;
}

.document-icon {
    font-size: 24px;
    color: #d32f2f;
    margin-right: 10px;
}

.document-name {
    word-break: break-word;
    flex-grow: 1;
}

.view-btn {
    margin-left: 10px;
    white-space: nowrap;
}

select[disabled] {
    background-color: #f8f9fa;
    cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .submitted-document {
        flex-direction: column;
        align-items: flex-start;
    }

    .view-btn {
        margin-left: 0;
        margin-top: 8px;
        align-self: flex-end;
    }
}
</style>
